import { create } from 'zustand';
import { TeamColor } from '../types/wargame-teams';
import { ActionType } from '../types/game';
import { useVictoryStore } from './victoryStore';
import { useTeamStore } from './teamStore';

export type TurnPhase = 'planning' | 'adjudication' | 'results' | 'complete';

export interface SubmittedAction {
  id: string;
  teamId: TeamColor;
  type: ActionType;
  targetId: string;
  params?: any;
  timestamp: number;
}

export interface AdjudicationResult {
  actionId: string;
  success: boolean;
  message: string;
  stateChanges: any[];
  cost: number;
}

interface AdjudicationStore {
  currentTurn: number;
  currentStage: number;
  phase: TurnPhase;
  
  // Timer
  timeRemaining: number; // seconds
  timerActive: boolean;
  
  // Actions
  teamActions: Map<TeamColor, SubmittedAction[]>;
  adjudicationResults: AdjudicationResult[];
  
  // Actions
  startTurn: () => void;
  submitAction: (teamId: TeamColor, action: Omit<SubmittedAction, 'id' | 'timestamp'>) => void;
  allActionsSubmitted: () => boolean;
  beginAdjudication: () => void;
  completeAdjudication: (results: AdjudicationResult[]) => void;
  endTurn: () => void;
  endStage: () => void;
  
  // Timer Actions
  tickTimer: () => void;
  setTimer: (seconds: number) => void;
  
  // Getters
  getGameProgress: () => number;
  getTeamActions: (teamId: TeamColor) => SubmittedAction[];
}

const PLANNING_TIME = 25 * 60; // 25 minutes
const ADJUDICATION_TIME = 30 * 60; // 30 minutes

export const useAdjudicationStore = create<AdjudicationStore>((set, get) => ({
  currentTurn: 1,
  currentStage: 1,
  phase: 'planning',
  timeRemaining: PLANNING_TIME,
  timerActive: false,
  teamActions: new Map(),
  adjudicationResults: [],

  startTurn: () => {
    set({
      phase: 'planning',
      timeRemaining: PLANNING_TIME,
      timerActive: true,
      teamActions: new Map(),
      adjudicationResults: []
    });
    // Notify teams?
  },

  submitAction: (teamId, action) => {
    const { teamActions } = get();
    const newAction: SubmittedAction = {
      ...action,
      id: Math.random().toString(36).substring(2, 9),
      timestamp: Date.now()
    };
    
    const currentActions = teamActions.get(teamId) || [];
    const newMap = new Map(teamActions);
    newMap.set(teamId, [...currentActions, newAction]);
    
    set({ teamActions: newMap });
  },

  allActionsSubmitted: () => {
    return true; 
  },

  beginAdjudication: () => {
    set({
      phase: 'adjudication',
      timeRemaining: ADJUDICATION_TIME,
      timerActive: true
    });
  },

  completeAdjudication: (results) => {
    set({
      phase: 'results',
      timerActive: false,
      adjudicationResults: results
    });
    
    // Check Victory Conditions
    useVictoryStore.getState().checkVictoryConditions();
  },

  endTurn: () => {
    const { currentTurn, currentStage, endStage } = get();
    
    // Rotate Team Turn if needed (though turns are simultaneous in this design)
    // useTeamStore.getState().rotateTeamTurn();

    if (currentTurn % 3 === 0) {
      endStage();
    } else {
      set({
        currentTurn: currentTurn + 1,
        phase: 'planning',
        timeRemaining: PLANNING_TIME,
        timerActive: true,
        teamActions: new Map(),
        adjudicationResults: []
      });
    }
  },

  endStage: () => {
    const { currentStage } = get();
    if (currentStage < 3) {
      set({
        currentStage: currentStage + 1,
        currentTurn: (currentStage * 3) + 1,
        phase: 'planning',
        timeRemaining: PLANNING_TIME,
        timerActive: true,
        teamActions: new Map(),
        adjudicationResults: []
      });
    } else {
      set({ phase: 'complete', timerActive: false });
    }
  },

  tickTimer: () => {
    const { timeRemaining, timerActive, phase, beginAdjudication } = get();
    if (!timerActive) return;

    if (timeRemaining > 0) {
      set({ timeRemaining: timeRemaining - 1 });
    } else {
      if (phase === 'planning') {
        beginAdjudication();
      } else if (phase === 'adjudication') {
        set({ timerActive: false });
      }
    }
  },

  setTimer: (seconds) => set({ timeRemaining: seconds }),

  getGameProgress: () => {
    const { currentTurn } = get();
    return ((currentTurn - 1) / 9) * 100;
  },

  getTeamActions: (teamId) => {
    return get().teamActions.get(teamId) || [];
  }
}));
